import type { PageDocument, PageMeta, SaveStrokesResponse, SessionDetail } from '@gn/shared';
import { ApiError, api, get, post } from './api';
import { drafts, pageKey, type LocalPage, type LocalSession } from './drafts';

export type SyncState = 'synced' | 'saved_local' | 'syncing' | 'offline' | 'conflict' | 'locked' | 'error';

type Listener = () => void;

/**
 * Offline-first synchronisation of vector pages (spec §11):
 *  - every edit is written to IndexedDB first ("Saved locally"),
 *  - dirty pages are pushed with their base revision and a client revision id (idempotent retries),
 *  - a 409 from the server becomes an explicit conflict the learner resolves — never last-write-wins.
 */
class SyncEngine {
  online = typeof navigator === 'undefined' ? true : navigator.onLine;
  private listeners = new Set<Listener>();
  private inflight = new Set<string>();
  private writeChain = new Map<string, Promise<void>>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private errors = new Map<string, string>();
  private cache = new Map<string, LocalPage>();
  /** Conflicts re-checked once this run (earlier false conflicts heal automatically). */
  private rechecked = new Set<string>();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.online = true;
        this.emit();
        void this.syncAll();
      });
      window.addEventListener('offline', () => {
        this.online = false;
        this.emit();
      });
      setInterval(() => void this.syncAll(), 15_000);
    }
  }

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private emit() {
    this.listeners.forEach((l) => l());
  }

  /** Synchronous status from the in-memory cache of local pages. */
  status(sessionId: string): SyncState {
    const pages = [...this.cache.values()].filter((p) => p.sessionId === sessionId);
    if (pages.some((p) => p.conflict)) return 'conflict';
    if (pages.some((p) => p.locked)) return 'locked';
    const dirty = pages.some((p) => p.localVersion !== p.syncedVersion);
    if (pages.some((p) => this.inflight.has(p.key))) return 'syncing';
    if (dirty && !this.online) return 'offline';
    if (dirty && this.errors.has(sessionId)) return 'error';
    if (dirty) return 'saved_local';
    return this.online ? 'synced' : 'offline';
  }

  lastError(sessionId: string) {
    return this.errors.get(sessionId) ?? null;
  }

  conflicts(sessionId: string): LocalPage[] {
    return [...this.cache.values()].filter((p) => p.sessionId === sessionId && p.conflict);
  }

  /**
   * The only way page records are changed: serialized per page and always applied to the latest stored
   * copy, so a background upload acknowledgement can never be overwritten by a stale local save
   * (that race produced false "changed on another device" conflicts).
   */
  private mutate(sessionId: string, pageId: string, fn: (cur: LocalPage | undefined) => LocalPage | undefined): Promise<LocalPage | undefined> {
    const key = pageKey(sessionId, pageId);
    const prev = this.writeChain.get(key) ?? Promise.resolve();
    const next = prev.then(async () => {
      const cur = await drafts.getPage(sessionId, pageId);
      const updated = fn(cur);
      if (updated) {
        await drafts.putPage(updated);
        this.cache.set(key, updated);
        this.emit();
      }
      return updated ?? cur;
    });
    this.writeChain.set(
      key,
      next.then(
        () => undefined,
        () => undefined,
      ),
    );
    return next;
  }

  /** Persist a page locally (fast) and schedule a sync. */
  savePage(sessionId: string, pageId: string, order: number, document: PageDocument): Promise<void> {
    const key = pageKey(sessionId, pageId);
    const p = this.mutate(sessionId, pageId, (existing) =>
      existing
        ? { ...existing, document, order, localVersion: existing.localVersion + 1, updatedAt: Date.now() }
        : {
            key,
            sessionId,
            pageId,
            order,
            document,
            baseRevision: 0,
            localVersion: 1,
            syncedVersion: 0,
            pendingRid: null,
            pendingForVersion: null,
            conflict: null,
            locked: null,
            sentRids: [],
            updatedAt: Date.now(),
          },
    );
    this.scheduleSync(1200);
    return p.then(() => undefined);
  }

  /** Wait until every pending local write has reached IndexedDB. */
  async flushLocal(): Promise<void> {
    await Promise.all([...this.writeChain.values()]);
  }

  private scheduleSync(delay: number) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.syncAll();
    }, delay);
  }

  async registerLocalSession(s: LocalSession) {
    await drafts.putSession(s);
  }

  private async ensureServerSession(sessionId: string): Promise<boolean> {
    const s = await drafts.getSession(sessionId);
    if (!s || s.serverCreated) return true;
    try {
      await post<SessionDetail>('/sessions', s.create);
      await drafts.putSession({ ...s, serverCreated: true });
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.isNetwork) {
        this.online = false;
        this.emit();
      }
      return false;
    }
  }

  async syncAll(): Promise<void> {
    if (this.running) return;
    this.running = true;
    try {
      await this.flushLocal();
      const pages = await drafts.allPages();
      for (const p of pages) this.cache.set(p.key, this.cache.get(p.key)?.localVersion === p.localVersion ? this.cache.get(p.key)! : p);
      const dirty = pages.filter((p) => p.localVersion !== p.syncedVersion && !p.locked && (!p.conflict || !this.rechecked.has(p.key)));
      for (const p of dirty) if (p.conflict) this.rechecked.add(p.key);
      for (const p of dirty) {
        if (!(await this.syncPage(p.sessionId, p.pageId))) break;
      }
    } finally {
      this.running = false;
      this.emit();
    }
  }

  async syncSession(sessionId: string): Promise<SyncState> {
    await this.flushLocal();
    const pages = await drafts.pages(sessionId);
    for (const p of pages) {
      this.cache.set(p.key, p);
      if (p.localVersion !== p.syncedVersion && !p.conflict && !p.locked) await this.syncPage(sessionId, p.pageId);
    }
    this.emit();
    return this.status(sessionId);
  }

  /** Push one page. Returns false when the network is down (stop the loop). */
  private async syncPage(sessionId: string, pageId: string, force = false, retried = false): Promise<boolean> {
    const key = pageKey(sessionId, pageId);
    if (this.inflight.has(key)) return true;
    await this.flushLocal();
    const initial = await drafts.getPage(sessionId, pageId);
    if (!initial || (initial.localVersion === initial.syncedVersion && !force)) return true;
    if (!(await this.ensureServerSession(sessionId))) return this.online;
    this.inflight.add(key);
    this.emit();
    try {
      // Reserve one client revision id per local version (retries of the same content are idempotent).
      const p = (await this.mutate(sessionId, pageId, (cur) =>
        cur && (cur.pendingForVersion !== cur.localVersion || !cur.pendingRid)
          ? { ...cur, pendingRid: crypto.randomUUID(), pendingForVersion: cur.localVersion }
          : undefined,
      ))!;
      const sentVersion = p.localVersion;
      const rid = p.pendingRid!;
      const res = await api<SaveStrokesResponse>(`/sessions/${sessionId}/pages/${pageId}/strokes`, {
        method: 'PUT',
        body: { clientRevisionId: rid, baseRevision: p.baseRevision, pageOrder: p.order, document: p.document, ...(force ? { force: true } : {}) },
      });
      await this.mutate(sessionId, pageId, (cur) =>
        cur
          ? {
              ...cur,
              baseRevision: Math.max(cur.baseRevision, res.revision),
              syncedVersion: Math.max(cur.syncedVersion, sentVersion),
              pendingRid: cur.localVersion === sentVersion ? null : cur.pendingRid,
              pendingForVersion: cur.localVersion === sentVersion ? null : cur.pendingForVersion,
              sentRids: [...(cur.sentRids ?? []), rid].slice(-50),
              conflict: null,
            }
          : undefined,
      );
      this.errors.delete(sessionId);
      this.online = true;
      return true;
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.isNetwork) {
          this.online = false;
          return false;
        }
        if (err.code === 'REVISION_CONFLICT') {
          const d = (err.details ?? {}) as { serverRevision?: number; serverUpdatedAt?: string; serverStrokeCount?: number; serverClientRevisionId?: string | null };
          const cur = await drafts.getPage(sessionId, pageId);
          if (cur && !retried && (await this.isOwnServerCopy(sessionId, pageId, cur, d.serverClientRevisionId ?? null))) {
            // The server copy is this device's own earlier upload: rebase silently and push again.
            await this.mutate(sessionId, pageId, (c) => (c ? { ...c, baseRevision: d.serverRevision ?? c.baseRevision, pendingRid: null, pendingForVersion: null, conflict: null } : undefined));
            this.inflight.delete(key);
            return this.syncPage(sessionId, pageId, false, true);
          }
          await this.mutate(sessionId, pageId, (c) =>
            c ? { ...c, conflict: { serverRevision: d.serverRevision ?? 0, serverUpdatedAt: d.serverUpdatedAt, serverStrokeCount: d.serverStrokeCount } } : undefined,
          );
        } else if (['SESSION_LOCKED', 'EXAM_TIME_EXPIRED', 'EXAM_NOT_ACTIVE'].includes(err.code)) {
          await this.mutate(sessionId, pageId, (c) => (c ? { ...c, locked: err.code } : undefined));
        } else if (err.code === 'NOT_FOUND') {
          const s = await drafts.getSession(sessionId);
          if (s) await drafts.putSession({ ...s, serverCreated: false });
          this.errors.set(sessionId, err.code);
        } else {
          this.errors.set(sessionId, err.code);
        }
      }
      return true;
    } finally {
      this.inflight.delete(key);
      this.emit();
    }
  }

  /**
   * True when the newer server copy came from this device: either its client revision id is one we
   * uploaded, or every stroke on the server is also in our local copy (an ancestor of what we have).
   */
  private async isOwnServerCopy(sessionId: string, pageId: string, cur: LocalPage, serverRid: string | null): Promise<boolean> {
    if (serverRid && (cur.sentRids?.includes(serverRid) || cur.pendingRid === serverRid)) return true;
    try {
      const server = await get<{ document: PageDocument }>(`/sessions/${sessionId}/pages/${pageId}/strokes`);
      const mine = new Set(cur.document.strokes.map((s) => s.id));
      return server.document.strokes.every((s) => mine.has(s.id));
    } catch {
      return false;
    }
  }

  /**
   * Merge server state into local storage when a session is opened (another device may have written).
   * Clean local copies are refreshed; dirty copies based on an older revision become conflicts.
   */
  async pull(sessionId: string, serverPages: PageMeta[]): Promise<LocalPage[]> {
    await this.flushLocal();
    const local = new Map((await drafts.pages(sessionId)).map((p) => [p.pageId, p]));
    for (const sp of serverPages) {
      const lp = local.get(sp.id);
      const clean = !lp || lp.localVersion === lp.syncedVersion;
      if (lp && !clean) {
        // Unsynced local edits: the upload decides (a 409 for this device's own earlier upload is
        // rebased silently; only a genuinely different server copy becomes a conflict).
        continue;
      }
      if (!lp || lp.baseRevision < sp.revision || lp.order !== sp.order) {
        const docRes =
          !lp || lp.baseRevision < sp.revision
            ? await get<{ revision: number; document: PageDocument }>(`/sessions/${sessionId}/pages/${sp.id}/strokes`)
            : { revision: lp.baseRevision, document: lp.document };
        const v = (lp?.localVersion ?? 0) + 1;
        const fresh: LocalPage = {
          key: pageKey(sessionId, sp.id),
          sessionId,
          pageId: sp.id,
          order: sp.order,
          document: docRes.document,
          baseRevision: docRes.revision,
          localVersion: v,
          syncedVersion: v,
          pendingRid: null,
          pendingForVersion: null,
          conflict: null,
          locked: null,
          updatedAt: Date.now(),
        };
        // Apply only if the page is still clean (no edit slipped in while we were fetching).
        const applied = await this.mutate(sessionId, sp.id, (c) => (!c || c.localVersion === c.syncedVersion ? { ...fresh, sentRids: c?.sentRids ?? [] } : undefined));
        if (applied) local.set(sp.id, applied);
      }
    }
    // Pages removed on the server and clean locally: drop them.
    for (const [id, lp] of local) {
      if (!serverPages.some((s) => s.id === id) && lp.localVersion === lp.syncedVersion && lp.baseRevision > 0) {
        await drafts.deletePage(sessionId, id);
        local.delete(id);
      }
    }
    const out = [...local.values()].sort((a, b) => a.order - b.order);
    for (const p of out) this.cache.set(p.key, p);
    this.emit();
    void this.syncSession(sessionId);
    return out;
  }

  async resolveConflict(sessionId: string, pageId: string, strategy: 'mine' | 'server' | 'merge'): Promise<LocalPage> {
    const p = await drafts.getPage(sessionId, pageId);
    if (!p?.conflict) throw new Error('no conflict');
    const server = await get<{ revision: number; document: PageDocument }>(`/sessions/${sessionId}/pages/${pageId}/strokes`);
    if (strategy === 'server') {
      await this.mutate(sessionId, pageId, (c) => {
        if (!c) return undefined;
        const v = c.localVersion + 1;
        return { ...c, document: server.document, baseRevision: server.revision, localVersion: v, syncedVersion: v, conflict: null, pendingRid: null, pendingForVersion: null };
      });
    } else if (strategy === 'merge') {
      // Strokes are immutable with unique ids, so a union is a well-defined merge (erasures on the
      // other device are kept as erased only if erased on both).
      await this.mutate(sessionId, pageId, (c) => {
        if (!c) return undefined;
        const ids = new Set(server.document.strokes.map((s) => s.id));
        const strokes = [...server.document.strokes, ...c.document.strokes.filter((s) => !ids.has(s.id))].sort((a, b) => a.createdAt - b.createdAt);
        const height = Math.max(c.document.height, server.document.height);
        return { ...c, document: { ...c.document, height, strokes }, baseRevision: server.revision, localVersion: c.localVersion + 1, conflict: null, pendingRid: null, pendingForVersion: null };
      });
      await this.syncPage(sessionId, pageId);
    } else {
      await this.mutate(sessionId, pageId, (c) => (c ? { ...c, baseRevision: server.revision, conflict: null, pendingRid: null, pendingForVersion: null } : undefined));
      await this.syncPage(sessionId, pageId, true);
    }
    const final = (await drafts.getPage(sessionId, pageId))!;
    this.cache.set(final.key, final);
    this.emit();
    return final;
  }

  /** Server revisions of every page, or null if anything is still unsynced. */
  async pageRevisions(sessionId: string): Promise<Record<string, number> | null> {
    await this.flushLocal();
    const pages = await drafts.pages(sessionId);
    if (pages.some((p) => p.localVersion !== p.syncedVersion || p.conflict)) return null;
    return Object.fromEntries(pages.map((p) => [p.pageId, p.baseRevision]));
  }

  async removePage(sessionId: string, pageId: string) {
    await drafts.deletePage(sessionId, pageId);
    this.cache.delete(pageKey(sessionId, pageId));
    this.emit();
  }

  /** Drop all in-memory state (after local data was cleared). */
  reset() {
    this.cache.clear();
    this.errors.clear();
    this.emit();
  }

  forget(sessionId: string) {
    for (const k of [...this.cache.keys()]) if (k.startsWith(`${sessionId}:`)) this.cache.delete(k);
  }
}

export const sync = new SyncEngine();

if (typeof document !== 'undefined') {
  // Save + sync when the app is backgrounded (iPad home button, app switcher, screen lock).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void sync.flushLocal().then(() => sync.syncAll());
    else void sync.syncAll();
  });
  window.addEventListener('pagehide', () => void sync.flushLocal());
}
